import { useState, type FormEvent } from 'react';
import { Prompt } from '../components/Prompt';
import { SectionHeader } from '../components/SectionHeader';
import { TerminalWindow } from '../components/TerminalWindow';
import { site } from '../data/site';
import { cx } from '../lib/cx';
import styles from './Contact.module.css';

const SERVICES = [
  'Web app',
  'Mobile app',
  'Backend / API',
  'Cloud / DevOps',
  'One of your products',
  'Something else',
] as const;

type Status = 'idle' | 'submitting' | 'success' | 'error';

/** Mirrors the API's limits (ContactRequest.NameMax / EmailMax / MessageMax) so a long
 *  entry is stopped in the browser instead of bouncing with "One of the fields is too long." */
const API_MAX = { name: 200, email: 254, message: 5000 } as const;

/** The service is prefixed to the message as "[Service] ", so reserve room for the longest one. */
const SERVICE_PREFIX_MAX = Math.max(...SERVICES.map((s) => `[${s}] `.length));
const MESSAGE_MAX = API_MAX.message - SERVICE_PREFIX_MAX;

type Fields = {
  name: string;
  email: string;
  service: (typeof SERVICES)[number];
  message: string;
  /** Honeypot: hidden from humans, filled by bots. The API drops submissions where it is set. */
  website: string;
};

const EMPTY: Fields = { name: '', email: '', service: 'Web app', message: '', website: '' };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Body for the public contact API: { name, email, company, phone, message, website }.
 * The form has no company/phone inputs, so those go empty; the chosen service is
 * folded into the message so it is not lost.
 */
function toPayload({ name, email, service, message, website }: Fields) {
  return {
    name: name.trim(),
    email: email.trim(),
    company: '',
    phone: '',
    message: `[${service}] ${message.trim()}`,
    website,
  };
}

async function send(fields: Fields): Promise<void> {
  const override = import.meta.env.VITE_CONTACT_ENDPOINT;
  const endpoint = override || site.contactEndpoint;

  // Bots fill the hidden field. Pretend it worked and drop the message.
  if (fields.website) return;

  // Local dev never hits the live API unless VITE_CONTACT_ENDPOINT is set explicitly.
  if (import.meta.env.DEV && !override) {
    await sleep(600);
    return;
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Language': 'en' },
    body: JSON.stringify(toPayload(fields)),
  });
  if (!res.ok) throw new Error(`Contact endpoint returned ${res.status}`);
}

export function Contact() {
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [status, setStatus] = useState<Status>('idle');

  const update =
    <K extends keyof Fields>(key: K) =>
    (e: { target: { value: string } }) =>
      setFields((f) => ({ ...f, [key]: e.target.value as Fields[K] }));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    try {
      await send(fields);
      setFields(EMPTY);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  const busy = status === 'submitting';

  return (
    <section id="contact" className="band">
      <div className={cx('container', 'split', styles.wrap)}>
        <div className={styles.left}>
          <SectionHeader loose prompt="$ ./contact.sh" title={<>Let&rsquo;s build something.</>} />
          <p className="copy">
            Tell us about your project. We read every message and reply personally.
          </p>
          <dl className={styles.details}>
            <div className={styles.detail}>
              <dt className={styles.key}>email</dt>
              <dd className={styles.val}>
                <a href={`mailto:${site.email}`}>[{site.email}]</a>
              </dd>
            </div>
            <div className={styles.detail}>
              <dt className={styles.key}>location</dt>
              <dd className={styles.val}>[{site.location}]</dd>
            </div>
          </dl>
        </div>

        <TerminalWindow
          title="seax@dev: ~/contact"
          controls={false}
          className={styles.right}
          bodyClassName={styles.body}
        >
          {status === 'success' ? (
            <div role="status" className={styles.status}>
              <Prompt path="~/contact">./send --message</Prompt>
              <div className={styles.ok}>✓ message delivered</div>
              <div className="copy">We&rsquo;ll get back to you soon.</div>
              <button type="button" className={styles.again} onClick={() => setStatus('idle')}>
                send another
              </button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={onSubmit} noValidate={false}>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label htmlFor="sx-name" className={styles.label}>
                    --name
                  </label>
                  <input
                    id="sx-name"
                    name="name"
                    type="text"
                    required
                    maxLength={API_MAX.name}
                    autoComplete="name"
                    placeholder="Jane Doe"
                    className={styles.input}
                    value={fields.name}
                    onChange={update('name')}
                    disabled={busy}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="sx-email" className={styles.label}>
                    --email
                  </label>
                  <input
                    id="sx-email"
                    name="email"
                    type="email"
                    required
                    maxLength={API_MAX.email}
                    autoComplete="email"
                    placeholder="jane@company.com"
                    className={styles.input}
                    value={fields.email}
                    onChange={update('email')}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="sx-service" className={styles.label}>
                  --service
                </label>
                <select
                  id="sx-service"
                  name="service"
                  className={styles.input}
                  value={fields.service}
                  onChange={update('service')}
                  disabled={busy}
                >
                  {SERVICES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label htmlFor="sx-msg" className={styles.label}>
                  --message
                </label>
                <textarea
                  id="sx-msg"
                  name="message"
                  rows={5}
                  required
                  maxLength={MESSAGE_MAX}
                  placeholder="What are you building?"
                  className={cx(styles.input, styles.textarea)}
                  value={fields.message}
                  onChange={update('message')}
                  disabled={busy}
                />
              </div>

              {/* Honeypot: visually hidden and skipped by assistive tech. */}
              <div className={styles.hp} aria-hidden="true">
                <label htmlFor="sx-website">Website</label>
                <input
                  id="sx-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={fields.website}
                  onChange={update('website')}
                />
              </div>

              <div className={styles.actions}>
                <button
                  type="submit"
                  className={cx('btn', 'btn--primary', styles.submit)}
                  disabled={busy}
                >
                  {busy ? 'sending...' : '$ send --message'}
                </button>
                {status === 'error' && (
                  <p role="alert" className={styles.error}>
                    ✗ failed: try again or email us at{' '}
                    <a href={`mailto:${site.email}`}>{site.email}</a>
                  </p>
                )}
              </div>
            </form>
          )}
        </TerminalWindow>
      </div>
    </section>
  );
}
